import { useEffect, useMemo } from 'react';
import { RepeatRuleDialog } from './components/forms/RepeatRuleDialog';
import { TagManagerDialog } from './components/forms/TagManagerDialog';
import { LinkEmailDialog } from './components/forms/LinkEmailDialog';
import { LinkedEmailViewerDialog } from '@/components/LinkedEmailViewerDialog';
import { useTodoStore } from '@/stores/todoStore';
import { useTodoViewStore } from '@/stores/todoViewStore';
import { useTodoModalStore } from '@/stores/todoModalStore';
import { useTodoActionsStore } from '@/stores/todoActionsStore';
import type { RepeatRuleState } from '@/types/repeat';

export function TodoModalHost() {
  const selectedTaskId = useTodoViewStore((state) => state.selectedTaskId);
  const task = useTodoStore((state) => (selectedTaskId ? state.tasks[selectedTaskId] ?? null : null));
  const tags = useTodoStore((state) => state.tags);
  const repeatDialogOpen = useTodoModalStore((state) => state.repeatDialogOpen);
  const tagManagerOpen = useTodoModalStore((state) => state.tagManagerOpen);
  const linkEmailDialogOpen = useTodoModalStore((state) => state.linkEmailDialogOpen);
  const closeRepeatDialog = useTodoModalStore((state) => state.closeRepeatDialog);
  const closeTagManager = useTodoModalStore((state) => state.closeTagManager);
  const closeLinkEmailDialog = useTodoModalStore((state) => state.closeLinkEmailDialog);
  const { saveTask, updateTaskTags, linkTaskEmail, unlinkTaskEmail, createTag, editTag, destroyTag } = useTodoActionsStore();

  const availableTags = useMemo(() => Object.values(tags), [tags]);

  useEffect(() => {
    if (!task) {
      if (repeatDialogOpen) {
        closeRepeatDialog();
      }
      if (tagManagerOpen) {
        closeTagManager();
      }
      if (linkEmailDialogOpen) {
        closeLinkEmailDialog();
      }
    }
  }, [
    closeLinkEmailDialog,
    closeRepeatDialog,
    closeTagManager,
    linkEmailDialogOpen,
    repeatDialogOpen,
    tagManagerOpen,
    task,
  ]);

  const handleRepeatSave = async (state: RepeatRuleState) => {
    if (!task) {
      return;
    }
    await saveTask(task.id, { repeat: state });
    closeRepeatDialog();
  };

  const handleTagSubmit = async (tagIds: string[]) => {
    if (!task) {
      return;
    }
    await updateTaskTags(task.id, tagIds);
    closeTagManager();
  };

  const handleLinkSubmit = async (link: { accountId: string; messageId: string }) => {
    if (!task) {
      return;
    }
    await linkTaskEmail(task.id, link.accountId, link.messageId);
    closeLinkEmailDialog();
  };

  const handleUnlink = async (link: { accountId: string; messageId: string }) => {
    if (!task) {
      return;
    }
    await unlinkTaskEmail(task.id, link.accountId, link.messageId);
  };

  return (
    <>
      <RepeatRuleDialog
        open={Boolean(task) && repeatDialogOpen}
        initialState={null}
        onClose={closeRepeatDialog}
        onSave={handleRepeatSave}
      />
      <TagManagerDialog
        open={Boolean(task) && tagManagerOpen}
        availableTags={availableTags}
        selectedTagIds={task?.tags.map((tag) => tag.id) ?? []}
        onClose={closeTagManager}
        onSubmit={handleTagSubmit}
        onCreateTag={createTag}
        onUpdateTag={editTag}
        onDeleteTag={async (tagId) => {
          await destroyTag(tagId);
        }}
      />
      <LinkEmailDialog
        open={Boolean(task) && linkEmailDialogOpen}
        emails={task?.emails ?? []}
        onClose={closeLinkEmailDialog}
        onSubmit={handleLinkSubmit}
        onUnlink={handleUnlink}
      />
      <LinkedEmailViewerDialog />
    </>
  );
}
